import React, { useEffect, useState } from 'react';
import { DAYS } from '../utils/Constants';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';

type TInputboxProps = {
    label: string;
    showAdditionalLabel?: boolean;
    onChange: (value: number) => void;
    initialValue?: number;
    'data-testid'?: string;
}

/**
 * "Set Test Duration" (`okf/design-system/e2e.md` § 144).
 *
 * Behaviour is unchanged, including the part that is odd: entering `''` or `'0'` raises
 * `toast.warning('Set duration should be greater than 0')` **and still commits the
 * value as 0**. The warning is advisory; the actual gate is `ScheduleSection`'s
 * `disabled={test_duration === 0}` on Run Test Now. Left exactly as it was — this is a
 * restyle, and changing a validation rule that decides whether a test can start is a
 * separate decision.
 *
 * The `*` marker was a bare `<span className="tw-text-red-500">` beside a `<label>` with
 * no `htmlFor`. `FieldTitle required` renders it `aria-hidden` (read aloud it is the
 * word "asterisk") and at the label's own type token — the 1.6px row-height defect
 * § 112 measured. Requiredness now also reaches assistive tech properly, via the
 * field's `required`.
 */
const InputboxLabel = ({ label, showAdditionalLabel = false, onChange, initialValue = 0, 'data-testid': dataTestId }: TInputboxProps) => {

    const [value, setValue] = useState<number>(initialValue);
    const inputId = React.useId();

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleChange = (newValue: string) => {
        if (newValue === '' || newValue === '0') {
            toast.warning('Set duration should be greater than 0');
        }
        setValue(newValue === '' ? 0 : parseFloat(newValue));
        onChange(newValue === '' ? 0 : parseFloat(newValue));
    };

    return (
        <div data-testid={dataTestId}>
            <FieldTitle label={label} required htmlFor={inputId} />
            <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
                <TextField
                    label=""
                    id={inputId}
                    aria-label={label}
                    required
                    type="number"
                    minValue={1}
                    className="tw-w-32"
                    value={value.toString()}
                    onChange={handleChange}
                    data-testid="components-inputboxlabel-input-1"
                />
                {showAdditionalLabel ? (
                    <Typography variant="body-primary" as="span" className="tw-text-ink">
                        {Math.abs(value) <= 1 ? 'DAY' : DAYS}
                    </Typography>
                ) : null}
            </div>
        </div>
    );
}

export default InputboxLabel
